import DocumentPage from './DocumentPage';

export const examDocument = {
  slug: 'exam',
  route: '/exam',
  title: 'Exam',
  category: 'Academics',
  format: 'DOCX',
  description:
    'The Exam module supports exam configuration, exam sessions, subject-wise exams, attendee result updates, result templates, and marksheet generation.',
  metrics: [
    { label: 'Primary focus', value: 'Exam governance' },
    { label: 'Core records', value: 'Types, rooms, sessions, exams' },
    { label: 'Evaluation', value: 'Normal and grade modes' },
  ],
  workflow: [
    'Configure exam types, exam rooms, and grade ranges before creating exam sessions.',
    'Create an exam session, add subject-wise exams, and move each exam through scheduling and held states.',
    'Update attendee results, generate result templates, and prepare marksheet registers, marksheet lines, and result lines.',
  ],
  sections: [
    {
      id: 'configure-exam',
      title: 'Configure Exam',
      body: [
        'EduX provides the facility to configure exam related details like Exam Types and Exam Rooms. The goal of giving a facility to configure the exam type is that an institute may take different types of exams, so it is better to keep these details configurable for administrators.',
        'Exam Type is used to configure the types of exams.',
        'Exam Room is used to specify the room details that can be used while exams are conducted in the institute.',
      ],
      subsections: [
        {
          id: 'configure-exam-types',
          title: 'Configure Exam Types',
          body: [
            'Go to Exams -> Configuration -> Exam Types and click New to create a new exam type.',
          ],
          figure: {
            src: '/assets/doc-images/exam/image.png',
            alt: 'Exam Type form showing sessional exam type',
            caption: 'Exam Types help administrators configure reusable assessment categories such as sessional or annual.',
          },
          bullets: [
            'Name: give the name of the exam type.',
            'Code: specify the code of the exam type. The code should not be more than 4 characters long.',
          ],
        },
        {
          id: 'configure-exam-rooms',
          title: 'Configure Exam Rooms',
          body: [
            'Go to Exams -> Configuration -> Exam Rooms and click New to create a new exam room.',
          ],
          figure: {
            src: '/assets/doc-images/exam/image2.png',
            alt: 'Exam Room form showing classroom and capacity',
            caption: 'Exam Rooms define the classroom and seating capacity available for examinations.',
          },
          bullets: [
            'Name: give the name of the exam room.',
            'Classroom: select the classroom from the list that can be used for taking exams.',
            'Capacity: specify how many persons can be seated in the classroom.',
          ],
        },
        {
          id: 'grade-configuration',
          title: 'Grade Configuration',
          body: [
            'Go to Exams -> Configuration -> Grade Configuration.',
            'It is used to define the grading system for exam results. It allows the administrator to set a percentage range and assign a corresponding result or grade.',
          ],
          figure: {
            src: '/assets/doc-images/exam/imagef.png',
            alt: 'Grade Configuration form with minimum and maximum percentage',
            caption: 'Grade Configuration maps a percentage range to a displayed result or grade.',
          },
          bullets: [
            'Minimum Percentage: the lowest percentage limit for the grade.',
            'Maximum Percentage: the highest percentage limit for the grade.',
            'Result to Display: the grade or result shown when a student score falls within the defined range.',
          ],
        },
      ],
    },
    {
      id: 'exam-sessions',
      title: 'Exam Sessions',
      body: [
        'Each institute conducts exams in its own way. EduX provides a way to create an exam session based on the course and batch.',
        'Go to Exams -> Exams -> Exam Sessions and click New to create a new Exam Session.',
      ],
      figure: {
        src: '/assets/doc-images/exam/image3.png',
        alt: 'Exam Session form with course batch dates and venue',
        caption: 'An Exam Session groups the complete exam cycle for a selected course, batch, and date range.',
      },
      detailCards: [
        {
          title: 'Details of Exam Sessions',
          bullets: [
            'Exam Session: provide a name for the exam session according to the exam being held.',
            'Exam Session Code: provide an appropriate code for the exam session. It should not be more than 8 characters long.',
            'Start Date and End Date: provide the dates on which the exam session starts and ends.',
            'Course: select the course for which the exam is to be taken.',
            'Batch: based on the selected course, choose the batch for which the exam is conducted.',
            'Exam Type: select whether the exam is sessional or annual.',
            'Evolution Type: select whether the evaluation is normal or grade based.',
            'Venue: select the room from the available list that will be used for the exam.',
          ],
        },
      ],
    },
    {
      id: 'exams',
      title: 'Exams',
      body: [
        'An Exam Session contains multiple exams. Each individual exam can be created here with respect to each subject.',
        'Go to Exams -> Exams -> Exams and click New to create a new Exam.',
      ],
      figure: {
        src: '/assets/doc-images/exam/image4.png',
        alt: 'Exam form in draft state with subject schedule and marks',
        caption: 'Each subject-wise exam is created under an exam session with timing and marks details.',
      },
      detailCards: [
        {
          title: 'Details of Exams',
          bullets: [
            'Exam Session: select the exam session under which this exam is contained.',
            'Exam: provide a reasonable exam name according to the exam being held.',
            'Subject: select the subject for which the exam is being held.',
            'Exam Code: provide an appropriate code for the exam. It should not be more than 8 characters long.',
            'Start Time and End Time: provide the start and end time of the exam.',
            'Total Marks and Passing Marks: provide the total marks and passing marks for the exam.',
          ],
        },
      ],
      subsections: [
        {
          id: 'workflow-of-exams',
          title: 'Workflow of Exams',
          body: [
            'Once the details are filled in and the form is saved, the exam starts from Draft state and moves through scheduling, held, result update, and done stages.',
          ],
        },
        {
          id: 'draft',
          title: 'Draft',
          body: [
            'Once the details are filled up in the exam form and it is saved, the state is Draft. The workflow of the form starts from here.',
          ],
          figure: {
            src: '/assets/doc-images/exam/image4.png',
            alt: 'Exam form in draft state',
            caption: 'Draft is the initial state after the exam record is created.',
          },
        },
        {
          id: 'scheduled',
          title: 'Scheduled',
          body: [
            'Click on Schedule Exam to schedule the exam. This changes the state of the record from Draft to Scheduled.',
          ],
          figure: {
            src: '/assets/doc-images/exam/image5.png',
            alt: 'Exam form in scheduled state',
            caption: 'Scheduled confirms that the exam timing and record are now formally scheduled.',
          },
        },
        {
          id: 'held',
          title: 'Held',
          body: [
            'Click on Held Exam to start the exam. It opens a wizard asking for attendee details.',
            'Click on Held in the wizard and this changes the state from Scheduled to Held.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/exam/image6.png',
              alt: 'Held Exam wizard for absent attendees and marks room note input',
              caption: 'The Held Exam wizard captures attendee details before the exam is marked as held.',
            },
            {
              src: '/assets/doc-images/exam/image7.png',
              alt: 'Exam form in held state',
              caption: 'Held means the exam has started and attendee data can be tracked against the exam.',
            },
          ],
        },
        {
          id: 'result-update',
          title: 'Result Update',
          body: [
            'Click on Result Update. This changes the state to Result Updated.',
          ],
          figure: {
            src: '/assets/doc-images/exam/image9.png',
            alt: 'Exam form in result updated state',
            caption: 'Result Updated indicates that result processing has been completed for the exam.',
          },
        },
        {
          id: 'done',
          title: 'Done',
          body: [
            'Click on Done. This changes the state to Done.',
          ],
          figure: {
            src: '/assets/doc-images/exam/imagea.png',
            alt: 'Exam form in done state',
            caption: 'Done is the final exam state after the evaluation workflow is completed.',
          },
        },
      ],
    },
    {
      id: 'exam-attendees',
      title: 'Exam Attendees',
      body: [
        'To update results, go to Exams -> Exams -> Exam Attendees and select attendees to update their result.',
        'Exam Attendees stores the exam-level attendance and marks details for each student.',
      ],
      figure: {
        src: '/assets/doc-images/exam/image8.png',
        alt: 'Exam Attendees list with student status marks and room',
        caption: 'Exam Attendees shows the student, exam, status, marks, room, and note for each attendee entry.',
      },
      detailCards: [
        {
          title: 'Details of Exams Attendee',
          bullets: [
            'Student: select the name of the student from the list of available students.',
            'Exam: select the exam from the list of exams.',
            'Status: choose whether the student is present or absent for the exam. By default Present is assigned.',
            'Marks: provide the marks earned by the student in that exam.',
            'Room: select the room in which the exam is conducted.',
            'Note: provide any note about the exam given by the student if required.',
          ],
        },
      ],
    },
    {
      id: 'result-template',
      title: 'Result Template',
      body: [
        'Go to Exams -> Exams -> Result Templates and click New to create a new Result Template.',
        'By default the template state is set to Draft. Click Generate Result and it changes to Result Generated.',
      ],
      figure: {
        src: '/assets/doc-images/exam/imageb.png',
        alt: 'Result Template form showing exam session and result date',
        caption: 'Result Templates define how results are generated for a selected exam session.',
      },
      bullets: [
        'Name: set the name of the result template.',
        'Result Date: set the result date. By default today’s date appears.',
        'Exam Session: select the exam session from the list.',
        'Grade Scale: after selecting the grade scale, the system shows grades automatically based on student marks.',
      ],
    },
    {
      id: 'marksheet-registers',
      title: 'Marksheet Registers',
      body: [
        'EduX provides the facility to maintain the marksheet of all students in a register called Marksheet Registers. It contains the marksheets of all students and the details of the exam conducted.',
        'Go to Exams -> Exams -> Marksheet Registers and click New to create a new Marksheet Register.',
      ],
      figure: {
        src: '/assets/doc-images/exam/imagec.png',
        alt: 'Marksheet Register form showing totals and linked marksheets',
        caption: 'Marksheet Registers hold the overall exam result register for a selected exam session.',
      },
      detailCards: [
        {
          title: 'Details of Marksheet Registers',
          bullets: [
            'Marksheet Register: provide the name of the marksheet register.',
            'Exam Session: select the exam session for which the marksheet register is prepared.',
            'Generated By: select the person who generated the marksheet register. By default the logged-in user appears.',
            'Generated Date: provide the date on which the marksheet is generated. By default today’s date appears.',
            'Total Pass: specify the total number of pass students.',
            'Total Fail: specify the total number of fail students.',
            'Result Template: select the result template for the marksheet register.',
            'Marksheets: contains the marksheet of each student displaying marks, percentage, grade, and status.',
          ],
        },
      ],
    },
    {
      id: 'marksheet-lines',
      title: 'Marksheet Lines',
      body: [
        'Marksheet of all the students is maintained in EduX. It stores the marks and results along with subject-wise marks of students.',
        'Go to Exams -> Exams -> Marksheet Lines and click New to create a new Marksheet Line.',
      ],
      figure: {
        src: '/assets/doc-images/exam/imaged.png',
        alt: 'Marksheet Line form with total marks percentage and subject-wise results',
        caption: 'Marksheet Lines store each student result summary together with the subject-wise result list.',
      },
      bullets: [
        'Student: select the student name from the list of available students.',
        'Total Marks, Percentage and Status: provide the total marks, percentage, and result status of the student.',
        'Results: contains the subject-wise result of all subjects of the selected student.',
      ],
    },
    {
      id: 'result-lines',
      title: 'Result Lines',
      body: [
        'EduX provides the facility to maintain the subject-wise marks details of all students.',
        'Go to Exams -> Exams -> Result Lines and click New to create a new Result Line.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/exam/imagee.png',
          alt: 'Result Line form for a student exam mark',
          caption: 'Result Lines store marks, status, and evaluation type for each subject-wise exam result.',
        },
        {
          src: '/assets/doc-images/exam/imagef.png',
          alt: 'Grade Configuration form referenced for result grading',
          caption: 'Grade Configuration supports the grade-based evaluation mode used in result lines.',
        },
      ],
      bullets: [
        'Student: select the name of the student whose result is to be prepared.',
        'Exam: select the exam from the list for which the result is prepared.',
        'Marks: provide the marks of the student in that exam.',
        'Status: status is set based on the entered value of marks.',
        'Evolution Type: select whether the evaluation is normal or grade based.',
      ],
    },
  ],
  related: ['attendance', 'assignments', 'timetable'],
};

export default function ExamPage() {
  return <DocumentPage document={examDocument} />;
}
